import {
  EnvironmentProviders,
  ErrorHandler,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import {
  provideRouter,
  RouterFeatures,
  Routes,
  withNavigationErrorHandler,
  withRouterConfig,
  withPreloading,
  PreloadAllModules
} from '@angular/router';
import { provideDateFnsAdapter } from '@angular/material-date-fns-adapter';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { PlatformType } from '@data-access';
import {
  provideDragDropDefaults,
  provideOverlayCompat,
  provideTooltipDefaults,
} from '@shared-app-config';
import { CABINET_TYPE } from '../constants/platform-type.constants';
import {
  ChunkReloadErrorHandler,
  reloadIntoFailedNavigation,
} from '../error-handlers/chunk-reload-error.handler';
import { RefreshTokenInterceptor } from '../interceptors/refresh-token.interceptor';
import { ServiceWorkInterceptor } from '../interceptors/service-work.interceptor';
import { TokenInterceptor } from '../interceptors/token.interceptor';
import { AppVersionService } from '../services/app-version.service';
import { CoreService } from '../services/core.service';
import { provideBuildInfoLog } from './build-info.providers';
import { provideErrorMonitoring } from './error-monitoring.providers';
import { provideDatePickerLocale, provideLocaleId, provideMatDateLocale } from './locale.providers';
import { provideRouteRenderOnActivation } from './route-render.providers';

/**
 * The application-wide providers every cabinet boots with; only the platform and routes differ.
 * `routerFeatures` add to the shared router setup (the login landing navigates between pages and
 * needs scroll restoration).
 */
export function provideCabinetApp(
  platform: PlatformType,
  routes: Routes,
  routerFeatures: RouterFeatures[] = [],
): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideZonelessChangeDetection(),
    // ngx-translate 18: замість `TranslateModule.forRoot` — провайдери, а поля
    // `defaultLanguage`/`useDefaultLang` замінені на `fallbackLang`. Активну мову як і раніше
    // ставить `LanguageService` через `use()`; тут — лише те, на що падати без ключа.
    provideTranslateService({ fallbackLang: 'en' }),
    // `useHttpBackend` зберігає те, що раніше робив ручний `new HttpClient(httpHandler)`:
    // переклади тягнуться в обхід DI-інтерсептора, щоб не тягнути на них авторизацію і
    // refresh-token логіку.
    provideTranslateHttpLoader({
      prefix: './assets/i18n/',
      suffix: '.json?v=' + Date.now(),
      useHttpBackend: true,
    }),
    // Angular 22 змінює дефолт paramsInheritanceStrategy на 'always'. Пінимо поточну
    // поведінку явно, щоб бамп фреймворку не почав тихо успадковувати батьківські
    // params і data — найперше в magic-links роутах.
    provideRouter(
      routes,
      withRouterConfig({ paramsInheritanceStrategy: 'emptyOnly' }),
      withNavigationErrorHandler(reloadIntoFailedNavigation),
      withPreloading(PreloadAllModules),
      ...routerFeatures,
    ),
    provideRouteRenderOnActivation(),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideDateFnsAdapter(),
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: RefreshTokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ServiceWorkInterceptor, multi: true },
    { provide: CABINET_TYPE, useValue: platform },
    { provide: ErrorHandler, useClass: ChunkReloadErrorHandler },
    provideLocaleId(),
    provideMatDateLocale(),
    provideDatePickerLocale(),
    provideOverlayCompat(),
    provideTooltipDefaults(),
    provideDragDropDefaults(),
    provideBuildInfoLog(),
    provideErrorMonitoring(),
    provideAppInitializer(() => inject(AppVersionService).checkVersionOnStartup()),
    provideAppInitializer(() => inject(CoreService).initializeApp()),
  ]);
}
